import React, { useEffect, useState } from 'react'
import {
  GoogleMap,
  Marker,
  Polyline,
  withGoogleMap,
  withScriptjs,
} from 'react-google-maps'
import PinSource from '../../assets/img/pin_source.png'
import PinDestination from '../../assets/img/pin_destination.png'
import { CustomLoader } from '../CustomLoader'
import GridContainer from '../Grid/GridContainer'
import { Coordinates, RegularMapWithDirectionsProps } from './interfaces'

const useDirections = (
  startPoint: Coordinates,
  endPoint: Coordinates,
  onDirectionsCalculated: (data: {
    distance_suggested: string
    time_end: string
  }) => void
) => {
  const [directions, setDirections] = useState<google.maps.LatLng[][]>([])
  const [distance, setDistance] = useState('-')
  const [time, setTime] = useState('-')

  useEffect(() => {
    if (!startPoint || !endPoint) {
      return
    }

    const DirectionsService = new window.google.maps.DirectionsService()

    const mainRoute = {
      origin: {
        lat: parseFloat(startPoint.latitude),
        lng: parseFloat(startPoint.longitude),
      },
      destination: {
        lat: parseFloat(endPoint.latitude),
        lng: parseFloat(endPoint.longitude),
      },
      travelMode: window.google.maps.TravelMode.DRIVING,
    }
    DirectionsService.route(mainRoute, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK && result) {
        const route = result.routes[0]
        const leg = route.legs[0]
        setDirections([route.overview_path])
        setDistance(leg.distance?.text ?? '')
        setTime(leg.duration?.text ?? '')
        onDirectionsCalculated({
          distance_suggested: String(leg.distance?.text),
          time_end: String(leg.duration?.text),
        })
      } else {
        setDirections([])
        setDistance('-')
        setTime('-')
        onDirectionsCalculated({
          distance_suggested: '',
          time_end: '',
        })
      }
    })
  }, [])

  return { directions, distance, time }
}

const MapComponent = withScriptjs(
  withGoogleMap(
    ({
      startPoint,
      endPoint,
      colorStroke = '#01abc2',
      onDirectionsCalculated,
    }: RegularMapWithDirectionsProps) => {
      const { directions } = useDirections(
        startPoint,
        endPoint,
        onDirectionsCalculated
      )
      return (
        <GridContainer direction="row">
          {/* @ts-ignore*/}
          <GoogleMap
            defaultZoom={14}                
            center={{
              lat: parseFloat(startPoint.latitude),
              lng: parseFloat(startPoint.longitude),
            }}
            defaultOptions={{
              scrollwheel: false,
            }}
          >
            <Marker
              key="start"
              defaultLabel=""
              defaultIcon={{
                url: PinSource,
                scaledSize: { width: 48, height: 48 },
                size: { width: 48, height: 48 },
                labelOrigin: new window.google.maps.Point(20, 15),
              }}
              position={{
                lat: parseFloat(startPoint.latitude),
                lng: parseFloat(startPoint.longitude),
              }}
            />
            <Marker
              key="end"
              defaultLabel=""
              defaultIcon={{
                url: PinDestination,
                scaledSize: { width: 48, height: 48 },
                size: { width: 48, height: 48 },
                labelOrigin: new window.google.maps.Point(20, 15),
              }}
              position={{
                lat: parseFloat(endPoint.latitude),
                lng: parseFloat(endPoint.longitude),
              }}
            />
            {directions.length > 0 && (
              <Polyline
                path={directions[0]}
                options={{
                  strokeOpacity: 0.7,
                  strokeColor: colorStroke,
                  strokeWeight: 7,
                }}
              />
            )}
                  {directions.length > 1 && (
                    <Polyline
                      path={directions[1]}
                      options={{
                        strokeOpacity: 0.7,
                        strokeWeight: 7,
                        strokeColor: '#099107',
                      }}
                    />
                  )}                
          </GoogleMap>
        </GridContainer>
      )
    }
  )
)

const RegularMapWithDirections: React.FC<RegularMapWithDirectionsProps> = (
  props
) => (
  <MapComponent
    {...props}
    googleMapURL="/googlemap"
    loadingElement={<CustomLoader />}
    containerElement={<div style={{ height: '400px' }} />}
    mapElement={<div style={{ height: '100%' }} />}
  />
)

export default RegularMapWithDirections
